import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "../../shared/api/http.js";
import type { CouplePhoto, EventRow, WeddingRow } from "../../shared/types.js";
import type { ThemeId } from "@smartvow/shared";

export interface WeddingDetail {
  wedding: WeddingRow;
  events: EventRow[];
  couplePhotos: CouplePhoto[];
}

export const weddingKeys = {
  list: ["weddings"] as const,
  detail: (id: string) => ["wedding", id] as const,
  members: (id: string) => ["wedding-members", id] as const,
};

export const fetchWeddingDetail = (id: string) =>
  http.get<WeddingDetail>(`/weddings/${id}`);

export const useWeddings = () =>
  useQuery({
    queryKey: weddingKeys.list,
    queryFn: () => http.get<{ items: WeddingRow[] }>("/weddings"),
  });

export const useWedding = (id: string) =>
  useQuery({ queryKey: weddingKeys.detail(id), queryFn: () => fetchWeddingDetail(id) });

export function useCreateWedding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { partner1Name: string; partner2Name: string }) =>
      http.post<{ id: string }>("/weddings", v),
    onSuccess: () => qc.invalidateQueries({ queryKey: weddingKeys.list }),
  });
}

export function useUpdateWedding(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<WeddingRow>) => http.patch(`/weddings/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: weddingKeys.detail(id) }),
  });
}

export function useDeleteWedding(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.del(`/weddings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: weddingKeys.list }),
  });
}

/* ---- photos du couple ---- */

async function uploadCouplePhoto(weddingId: string, file: File) {
  const init = await http.post<{ key: string; presigned: { urls: { url: string }[] } }>(
    `/weddings/${weddingId}/couple-photos/init`,
    { contentType: file.type, bytes: file.size },
  );
  const put = await fetch(init.presigned.urls[0]!.url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!put.ok) throw new Error(`upload ${put.status}`);
  await http.post(`/weddings/${weddingId}/couple-photos`, { key: init.key });
}

export function useCouplePhotoMutations(id: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: weddingKeys.detail(id) });

  const add = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadCouplePhoto(id, f);
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (photoId: string) => http.del(`/weddings/${id}/couple-photos/${photoId}`),
    onSuccess: invalidate,
  });
  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) =>
      http.patch(`/weddings/${id}/couple-photos/order`, { orderedIds }),
    onSuccess: invalidate,
  });
  return { add, remove, reorder };
}

/* ---- membres (accès partagé au mariage) ---- */

export interface WeddingMember {
  id: string;
  name: string;
  email: string;
  createdAt: string;
}
export interface WeddingMembersResp {
  owner: { id: string; name: string; email: string } | null;
  members: WeddingMember[];
  canManage: boolean;
}

export const useWeddingMembers = (weddingId: string) =>
  useQuery({
    queryKey: weddingKeys.members(weddingId),
    queryFn: () => http.get<WeddingMembersResp>(`/weddings/${weddingId}/members`),
  });

export function useInviteMember(weddingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      name: string;
      email: string;
      mode: "password" | "invite";
      password?: string;
    }) => http.post<{ userId: string; inviteUrl: string | null }>(
      `/weddings/${weddingId}/members`,
      v,
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: weddingKeys.members(weddingId) }),
  });
}

export function useRemoveMember(weddingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => http.del(`/weddings/${weddingId}/members/${memberId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: weddingKeys.members(weddingId) }),
  });
}

/* ---- événement ---- */

export function useCreateEvent(weddingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      name: string;
      date: string;
      location: string;
      theme: ThemeId;
      captureStartAt?: string;
      captureEndAt?: string;
    }) => http.post<{ id: string }>(`/weddings/${weddingId}/events`, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: weddingKeys.detail(weddingId) }),
  });
}
